// Корневой компонент.
// Иерархия провайдеров:
//   QueryClientProvider — react-query (server state).
//     ConfigProvider — Ant Design theme + locale.
//       AntApp — context-aware message / notification / modal.
//         RouterProvider — react-router routes.
//           AppLayout (внутри ProtectedRoute) → рендерит GlobalErrorListener
//           (для useNavigate-context'а — см. AppLayout.tsx).
import { ConfigProvider, App as AntApp } from "antd";
import ruRU from "antd/locale/ru_RU";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { router } from "./router";
import { buildThemeConfig } from "./styles/theme";
import { useTheme } from "./hooks/useTheme";

// Один глобальный QueryClient на приложение.
// retries: 1 — большинство сетевых сбоев лечит axios сам через interceptor.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

export function App() {
  const { isDark } = useTheme();
  const themeConfig = useMemo(() => buildThemeConfig(isDark), [isDark]);

  // Тема ПРИЛОЖЕНИЯ (тумблер в шапке, useTheme) — на <html data-theme="...">.
  // Аддитивно: атрибут только ДОБАВЛЯЕТСЯ, ничего не убирает и не меняет для
  // страниц, которые его не читают (баннеры/обложки/анонсы — им всё равно).
  // Нужен обычному CSS, у которого нет доступа к React-состоянию: экран
  // правок корректуры (diffDocument.css) следит именно за этим атрибутом, а
  // не за системной темой ОС (prefers-color-scheme) — тумблер темы работает
  // независимо от темы ОС, и раньше эти два источника расходились (баг,
  // 2026-09-16). Пока JS не отработал (доля секунды до первого эффекта) CSS
  // фолбэчится на системную тему — см. diffDocument.css.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
  }, [isDark]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={ruRU}
        theme={themeConfig}
        modal={{ mask: { blur: false } }}
        drawer={{ mask: { blur: false } }}
      >
        <AntApp>
          <RouterProvider router={router} />
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
